// App Factory: one run takes an app from the AI wizard to a publisher handoff
// link (Firebase project, domain, privacy policy, builds on the way).
import { api } from './client';
import type { WizardPlan, createWizardJob, DomainRule } from './apps-setup';

export type FactoryStepKey = 'meta' | 'review' | 'firebase' | 'domain' | 'policy' | 'loader' | 'build' | 'handoff' | 'access' | 'publish';
export type FactoryStepStatus = 'waiting' | 'running' | 'needs_you' | 'done' | 'failed' | 'skipped';

export interface FactoryStep { key: FactoryStepKey; status: FactoryStepStatus; detail: string | null }

export interface FactoryRun {
  id: string;
  status: 'running' | 'done' | 'cancelled';
  created_by: string;
  created_at: number;
  updated_at: number;
  finished_at: number | null;
  /** null: the run was started on an existing app. */
  wizard_job_id: string | null;
  app_id: string | null;
  name: string | null;
  bundle_id: string | null;
  icon_url: string | null;
  /** null on an outside client's run (see isClientRun). */
  publisher_id: string | null;
  /** The client company of an outside client's run; null on ours. */
  client_id: string | null;
  client_name: string | null;
  publisher_name: string | null;
  publisher_has_chat: boolean;
  publish_cents: number;
  rent_cents: number;
  send_to_publisher: boolean;
  approved_at: number | null;
  contact_email: string | null;
  domain: string | null;
  domain_skippable: boolean;
  aab_build_id: string | null;
  apk_build_id: string | null;
  handoff_expires_at: number | null;
  /** Set when the run publishes the app itself through First publication. */
  play_account_id: number | null;
  play_account_name: string | null;
  /** The browser profile of a shared console: its Firebase project is made there. */
  shared_profile_name: string | null;
  publish_job_id: string | null;
  /** Publishing in the publisher's own console: the Gmail we sent and where they are with it. */
  console_access: boolean;
  console_gmail: string | null;
  console_access_state: 'preparing' | 'sent' | 'checking' | 'ok' | 'refused' | null;
  steps: FactoryStep[];
  /** Detail only. */
  handoff_url?: string | null;
}

/** An outside client's run, as our staff see it. */
export function isClientRun(run: Pick<FactoryRun, 'client_id'>): boolean {
  return !!run.client_id;
}

export interface FactoryPools {
  firebase: { profiles: number; free_slots: number; busy: number };
  domains: { enabled: boolean; reason: string | null };
  builds: { enabled: boolean };
  runs: { running: number; awaiting_review: number };
}

export type FactoryWizardInput = Parameters<typeof createWizardJob>[0];

const BASE = '/admin/apps-setup/factory';

export const listFactoryRuns = (status?: string) =>
  api.get<FactoryRun[]>(`${BASE}/runs${status ? `?status=${status}` : ''}`);
export const getFactoryRun = (id: string) => api.get<FactoryRun>(`${BASE}/runs/${id}`);
export const getFactoryPools = () => api.get<FactoryPools>(`${BASE}/pools`);

/** Publish it ourselves: on this Play account, with this template's answers. */
export interface FactoryPublishOn { play_account_id?: number | null; publish_template_id?: number | null; console_access?: boolean }

/** Own domain where the repo type leaves it optional; the policy's address when there is none (else the console's Gmail). */
export interface FactoryDomainInput { own_domain?: boolean; contact_email?: string }

export const createFactoryRun = (input: {
  publisher_id: string; publish_cents?: number; rent_cents?: number; send_to_publisher: boolean;
  wizard: FactoryWizardInput;
} & FactoryPublishOn & FactoryDomainInput) => api.post<{ run_id: string; job_id: string; plan: WizardPlan }>(`${BASE}/runs`, input);

/** An existing app a run can finish, with what it still lacks. */
export interface FactoryCandidate {
  id: string;
  name: string;
  bundle_id: string;
  icon_url: string | null;
  has_firebase: boolean;
  domain: string | null;
  has_policy: boolean;
  type_name: string | null;
  domain_rule: DomainRule | null;
  builds: number;
  last_run_at: number | null;
}

export const listFactoryCandidates = (q: string) =>
  api.get<FactoryCandidate[]>(`${BASE}/candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`);

export const createFactoryRunForApp = (input: {
  app_id: string; publisher_id: string; publish_cents?: number; rent_cents?: number; send_to_publisher: boolean;
} & FactoryPublishOn & FactoryDomainInput) => api.post<{ run_id: string }>(`${BASE}/runs`, input);

export const nudgeFactoryRun = (id: string) => api.post<{ queued: boolean }>(`${BASE}/runs/${id}/nudge`);
export const approveFactoryRun = (id: string) => api.post<{ approved: boolean }>(`${BASE}/runs/${id}/approve`);
export const retryFactoryStep = (id: string, step: FactoryStepKey) =>
  api.post<{ queued: boolean }>(`${BASE}/runs/${id}/retry`, { step });
export const skipFactoryDomain = (id: string, contactEmail: string) =>
  api.post<{ skipped: boolean }>(`${BASE}/runs/${id}/skip-domain`, { contact_email: contactEmail });
export const cancelFactoryRun = (id: string) => api.post<{ cancelled: boolean }>(`${BASE}/runs/${id}/cancel`);

// ---- outside clients ----------------------------------------------------------------
// A client company sees its own runs only, in fixed wording: no publisher,
// no accounts of ours, no prices. Same endpoints; the server scopes them.

export type ClientFactoryStepKey = 'meta' | 'review' | 'services' | 'domain' | 'policy' | 'loader' | 'build' | 'ready';

export interface ClientFactoryStep { key: ClientFactoryStepKey; status: FactoryStepStatus; detail: string | null }

export interface ClientFactoryRun {
  id: string;
  status: 'running' | 'done' | 'cancelled';
  created_at: number;
  updated_at: number;
  finished_at: number | null;
  wizard_job_id: string;
  /** Set once the app is created from the review screen. */
  app_id: string | null;
  name: string | null;
  bundle_id: string | null;
  icon_url: string | null;
  /** The support address on the privacy policy; null until one is made up on the domain. */
  contact_email: string | null;
  domain: string | null;
  /** False once the build is under way: the run is then ours to finish. */
  can_cancel: boolean;
  steps: ClientFactoryStep[];
}

/** The client's allowances this month and whether a run can start now. */
export interface ClientFactoryPools {
  can_start: boolean;
  reason: string | null;
  running: number;
  running_limit: number;
  runs_used: number;
  runs_limit: number;
  domains_used: number;
  domains_limit: number;
}

export const listClientFactoryRuns = () => api.get<ClientFactoryRun[]>(`${BASE}/runs`);
export const getClientFactoryRun = (id: string) => api.get<ClientFactoryRun>(`${BASE}/runs/${id}`);
export const getClientFactoryPools = () => api.get<ClientFactoryPools>(`${BASE}/pools`);

/**
 * The stub (wizard.base_repo_id) must be one marked for clients; `own_domain`
 * counts only where its repo type leaves the domain optional. Without
 * `contact_email` a run on an own domain makes one up there.
 */
export const createClientFactoryRun = (input: { wizard: FactoryWizardInput; own_domain?: boolean; contact_email?: string }) =>
  api.post<{ run_id: string; job_id: string; plan: WizardPlan }>(`${BASE}/runs`, input);
